import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
} from "@mui/material";

const DATE_PICKER_FIELDS = [
  "Diabetes",
  "Heart Disease",
  "Hypertension",
  "Hyperlipidemia",
  "Osteoarthritis",
  "Asthma/COPD/Bronchitis",
  "Cancer",
  "Alcohol or drug abuse",
  "Any HIV or STD/Related ailment",
  "Duration of the present ailment"
];

const ValidationRules = ({
  validationRules = [],
  onToggleApproval,
  onSubmitRules,
  onDateChange,
  isShowSTPData,
  isShowForDischarge,
  disabled = true,
  showSubmitButton = true,
}) => {
  const [selectedDates, setSelectedDates] = useState({});

  // Convert ISO date string to YYYY-MM-DD format for input[type="date"]
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";
      return d.toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  // Initialize selectedDates from rule.duration values
  useEffect(() => {
    const initial = {};
    validationRules.forEach((rule, index) => {
      if (rule.duration) {
        initial[index] = formatDateForInput(rule.duration);
      }
    });
    setSelectedDates(initial);
  }, [validationRules]);

  const shouldShowDatePicker = (fieldName) => {
    return DATE_PICKER_FIELDS.some(
      (f) => fieldName && fieldName.toLowerCase().includes(f.toLowerCase())
    );
  };

  const handleDateChange = (index, date, rule) => {    
    setSelectedDates((prev) => {
      const updated = { ...prev, [index]: date };
      // Build complete updated array with duration, edited, approved
      const updatedRules = validationRules.map((r, i) => {
        if (updated[i]) {
          return { ...r, duration: updated[i], edited: true, approved: r.approved };
        }
        return { ...r ,  edited: true, approved: r.approved};
      });
      // console.log("Updated rules array:", updatedRules);
      if (onDateChange) {
        onDateChange(updatedRules);
      }
      return updated;
    });
  };
  // Empty state
  if (validationRules.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "300px",
        }}
      >
        <Typography variant="h6" sx={{ color: "#666", mb: 1 }}>
          No Validation Rules
        </Typography>
        <Typography variant="body2" sx={{ color: "#999" }}>
          There are no validation rules available for this claim.
        </Typography>
      </Box>
    );
  }

   // Discharge Data View - Show data in card format in blue color
  if (isShowForDischarge) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Discharge Data
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
            gap: 2,
          }}
        >
          {validationRules.map((rule, index) => (
            <Paper
              key={index}
              elevation={2}
              sx={{
                p: 1.5,
                borderRadius: 2,
                borderLeft: "4px solid #1976d2",
                transition: "transform 0.2s, box-shadow 0.2s",
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                height: 90,
                boxSizing: "border-box",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 4,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontWeight: 600,
                    fontSize: "10px",
                  }}
                >
                  #{index + 1}
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: "12px",
                  color: "#333",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  mb: 0.5,
                }}
                title={rule.fieldName.toUpperCase() || "N/A"}
              >
               <span style={{ fontWeight: 600, color: "#1976d2" }}>Name: </span> {rule.fieldName|| "N/A"}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontSize: "11px",
                  color: "#666",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={rule.preAuthDocValue || "N/A"}
              >
                <span style={{ fontWeight: 600, color: "#1976d2" }}>Value: </span>
                {rule.preAuthDocValue || "N/A"}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }


  // STP Data View - Show data in card format, separated by approved status
  if (isShowSTPData) {
    const approvedRules = validationRules.filter((rule) => rule.approved);
    const notApprovedRules = validationRules.filter((rule) => !rule.approved);
    
    // Calculate summary statistics
    const totalFields = validationRules.length;
    const verifiedCount = validationRules.filter((rule) => rule.preAuthDocValue === "true" || rule.preAuthDocValue === true).length;
    const declinedCount = validationRules.filter((rule) => rule.preAuthDocValue === "false" || rule.preAuthDocValue === false).length;
    const missingCount = validationRules.filter((rule) => !rule.preAuthDocValue || rule.preAuthDocValue === "" || rule.preAuthDocValue === null || rule.preAuthDocValue === undefined).length;
    const overallVerified = missingCount === 0 && declinedCount === 0;

    const getRemarkText = (value) => {
      if (value === 'true' || value === 'false') return "Mentioned";
      if (!value || value === "" || value === null || value === undefined) return "Blank or Strike out mentioned";
      return String(value);
    };
    
    const isValueValid = (value) => {
      if (typeof value === "boolean") return value;
      return value && value !== "" && value !== null && value !== undefined;
    };

    const renderCards = (rules, startIndex = 0) => (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 2,
        }}
      >
        {rules.map((rule, index) => {
          const isValid = isValueValid(rule.preAuthDocValue);
          const remarkText = getRemarkText(rule.preAuthDocValue);          
          return (
            <Paper
              key={startIndex + index}
              elevation={2}
              sx={{
                p: 1.5,
                borderRadius: 3,
                border: `1px solid ${rule.approved ? "#22c55e" : "#ef4444"}`,
                borderLeft: `5px solid ${rule.approved ? "#22c55e" : "#ef4444"}`,
                backgroundColor: rule.approved ? "#f0fdf4" : "#fef2f2",
                transition: "transform 0.2s, box-shadow 0.2s",
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                minHeight: 100,
                boxSizing: "border-box",
                position: "relative",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 4,
                },
              }}
            >
              {/* Status Icon */}
              <Box
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  backgroundColor: rule.approved ? "#22c55e" : "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "16px",
                  fontWeight: 700,
                }}
              >
                {rule.approved ? "✓" : "−"}
              </Box>

              {/* Field Label */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: "10px",
                  color: "#666",
                  fontWeight: 500,
                  display: "block",
                  mb: 0.25,
                }}
                style={{ fontSize: "14px" }}
              >
                Field Label
              </Typography>

              {/* Field Name */}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontSize: "14px",
                  color: "#333",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  mb: 0.5,
                  pr: 4,
                }}
                title={rule.fieldName || "N/A"}
              >
                {rule.fieldName || "N/A"}
             
              </Typography>

              {/* Remark Label */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: "10px",
                  color: "#666",
                  fontStyle: "italic",
                  display: "block",
                  mb: 0.25,
                }}
                 style={{ fontSize: "14px" }}
              >
                Remark
              </Typography>

              {/* Remark Value */}
              {rule.fieldName !== "Installment policy" && rule.fieldName !== "Receipt Clearance" && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "11px",
                    color: isValid ? "#16a34a" : "#dc2626",
                    fontStyle: "italic",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
              title={remarkText}>
            
              {typeof rule.preAuthDocValue && (
                <span>{ rule.preAuthDocValue === 'true' || rule.preAuthDocValue === 'false'? "✓ | ✗ | Yes | No"  : ''}</span>
              )}
              {!isValid && "— "}
              {remarkText}
            </Typography>)}
             {rule.fieldName === "Name of the Patient" ||rule.fieldName === "Date of admission" ? ( <small style={{ fontSize: "12px", fontWeight: 600, color: "#f10707ff" }}>
                { rule.reason }
                
              </small>) : null}
            { rule.fieldName === "Installment policy" || rule.fieldName === "Receipt Clearance" ? (
              <small style={{ fontSize: "12px", fontWeight: 600, color: "#0ff107ff" }}>
                {<span>{rule.fieldName === "Installment policy" ? rule.insurerApiValue : ''}</span>}
                {<span>{rule.fieldName === "Receipt Clearance" ? rule.insurerApiValue : ''}</span>}
              </small>
            ) : null}
            </Paper>
          );
        })}
      </Box>
    );

    return (
      <Box sx={{ p: 2 }}>
      

        {/* Summary Overview Bar */}
        <Paper
          elevation={1}
          sx={{
            p: 1.5,
            mb: 3,
            borderRadius: 3,
            background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {/* Summary Overview Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                backgroundColor: "#e3f2fd",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ fontSize: "16px" }}>📋</span>
            </Box>
            <Typography variant="subtitle1" sx={{  fontSize: "24px", fontWeight: 700, color: "#333" }}>
                STP Validation Data Overview
            </Typography>
          </Box>

          {/* Stats - Arrow Steps */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* Step 1 - Total Fields (Start with > shape on left) */}
            <Box
              sx={{
                position: "relative",
                backgroundColor: "#6b7280",
                color: "#fff",
                px: 2.5,
                py: 0.75,
                fontWeight: 600,
                fontSize: "13px",
                clipPath: "polygon(15px 0, calc(100% - 15px) 0, 100% 50%, calc(100% - 15px) 100%, 15px 100%,  30px 50%)",
                minWidth: "130px",
                textAlign: "center",
              }}
            >
              <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>
                Total Fields: {totalFields}
              </Typography>
            </Box>

         
            <Box
              sx={{
                position: "relative",
                backgroundColor: "#22c55e",
                color: "#fff",
                px: 2.5,
                py: 0.75,
                fontWeight: 600,
                fontSize: "13px",
                marginLeft: "-1px",
                clipPath: "polygon(15px 0, calc(100% - 15px) 0, 100% 50%, calc(100% - 15px) 100%, 15px 100%, 30px 50%)",
                minWidth: "150px",
                textAlign: "center",
              }}
            >
              <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>
             Matched : ({approvedRules.length})
              </Typography>
            </Box>

          

     
            <Box
              sx={{
                position: "relative",
                backgroundColor: "#f72e0bff",
                color: "#fff",
                px: 2.5,
                py: 0.75,
                fontWeight: 600,
                fontSize: "13px",
                marginLeft: "-1px",
                clipPath: "polygon(15px 0, calc(100% - 15px) 0, 100% 50%, calc(100% - 15px) 100%, 15px 100%, 30px 50%)",
                minWidth: "140px",
                textAlign: "center",
              }}
            >
              <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>
                  Mismatched : ({notApprovedRules.length})
              </Typography>
            </Box>
          </Box>

          {/* Overall Verification */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width:'340px'}}>
          
           
          </Box>
        </Paper>

        <Box
          sx={{
            display: "flex",
            gap: 3,
          }}
        >
          {/* Matched (Approved) Section */}
          <Box sx={{ flex: 1 }}>           
            {approvedRules.length > 0 ? (
              renderCards(approvedRules)
            ) : (
              <Typography variant="body2" sx={{ color: "#999" }}>
                No matched rules
              </Typography>
            )}
          </Box>

          {/* Mismatched (Not Approved) Section */}
          <Box sx={{ flex: 1 }}>
            
            {notApprovedRules.length > 0 ? (
              renderCards(notApprovedRules)
            ) : (
              <Typography variant="body2" sx={{ color: "#999" }}>
                No mismatched rules
              </Typography>
            )}
          </Box>
        </Box>

        {showSubmitButton && (
          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              disabled={disabled}
              onClick={onSubmitRules}
            >
              Submit Rules
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  // Default View - Original compact table format
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Validation Rules
      </Typography>

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600 }}>Field Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Value Compare</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
                Date
              </TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
                Approved
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {validationRules.map((rule, index) => (
              <TableRow
                key={index}
                sx={{
                  backgroundColor: "#f5f5f5",
                  "&:hover": { backgroundColor: "#ffffffff" },
                }}
              >
                <TableCell>
                  <span
                    style={{ fontSize: "14px", display: "block", marginRight: 8 }}
                  >
                    {rule.fieldName}
                  </span>

              { rule.reason &&  <Box 
                    sx={{
                      display: "inline-block",
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 1,
                      fontSize: "10px",
                      fontWeight: 600,
                      backgroundColor: "#212121",
                      color: "#fff",
                    }}
                  >
             
                    {rule.reason}
                
                  </Box>}
                </TableCell>
                <TableCell>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontWeight: 600, color: "#01070eff" }}>
                        From Doc:
                      </span>
                      <span>{rule.preAuthDocValue || "N/A"}</span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontWeight: 600, color: "#01070eff" }}>
                        From Claim:
                      </span>
                      <span>{rule.insurerApiValue || "N/A"}</span>
                    </div>
                  </div>
                </TableCell>
                <TableCell sx={{ textAlign: "center", p: 0.5 }}>
                  {shouldShowDatePicker(rule.fieldName) ? (
                    <div style={{ position: "relative", display: "inline-block" }}>
                 
                      <input
                        type="date"
                        disabled={!rule.approved}
                        value={selectedDates[index] || formatDateForInput(rule.duration) || ""}
                        onChange={(e) => handleDateChange(index, e.target.value, rule)}
                        style={{
                          width: "100px",
                          padding: "2px 4px",
                          fontSize: "11px",
                          border: `1px solid ${rule.approved ? "#1976d2" : "#ccc"}`,
                          borderRadius: "3px",
                          outline: "none",
                          cursor: rule.approved ? "pointer" : "not-allowed",
                          opacity: rule.approved ? 1 : 0.4,
                          backgroundColor: rule.approved ? "#fff" : "#f5f5f5",
                        }}
                      />
                    </div>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell sx={{ textAlign: "center" }}>
                  <Switch
                    id={`approval-switch-${index}`}
                    checked={rule.approved}
                    disabled={disabled}
                    onChange={() => {
                      if (onToggleApproval) onToggleApproval(index);
                      // Also update onDateChange with toggled approved value
                      if (onDateChange) {
                        const updatedRules = validationRules.map((r, i) => {
                          const newApproved = i === index ? !r.approved : r.approved;
                          return {
                            ...r,
                            approved: newApproved,
                            edited: true,
                            duration: selectedDates[i] || formatDateForInput(r.duration) || r.duration || null,
                          };
                        });
                        onDateChange(updatedRules);
                      }
                    }}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#22c55e",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: "#22c55e",
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* {showSubmitButton && (
        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            disabled={disabled}
            onClick={onSubmitRules}
          >
            Submit Rules
          </Button>
        </Box>
      )} */}
    </Box>
  );
};

export default ValidationRules;
